import { ReportMetadata, ServiceItem, ServiceTransaction, UserProfile } from '../types';
import { loadProjectById, saveProjectToCloud } from './cloudService';
import { getServiceLog, mirrorEntriesToJournal } from './serviceJournal';
import { autoArchiveVerdict } from './projectAutoArchive';
import { istUebungsprojekt } from './uebung/uebungService';
import { pruefeArchivierbarkeitFuerStand } from './archiveGuardService';
import {
    MANUAL_BILLING_REFERENCE_PREFIX,
    ManualBillingPlan,
    planManualBilling,
    planManualBillingUndo,
} from './manualBillingLogic';

/**
 * Schreibpfad der MANUELLEN VERBUCHUNG — die einzige Stelle, die sie
 * persistiert.
 *
 * Die REGELN liegen daneben in `manualBillingLogic.ts` und sind ohne Supabase
 * pruefbar; hier steht nur, was ohne Datenbank nicht zu haben ist. Dasselbe
 * Muster wie serviceWaiverLogic/serviceWaiverService.
 *
 * Geschrieben wird ueber `saveProjectToCloud` und nicht per direktem UPDATE auf
 * `project_services`: die Position lebt in ZWEI Quellen (relational
 * `project_services` und die JSON-Spiegelung in
 * `data->metadata->globalData->services`), und `getNormalizedServices`
 * bevorzugt die relationale und faellt still auf das JSON zurueck. Bliebe eine
 * stehen, spraenge dieselbe Zelle je Ladepfad zwischen zwei Werten.
 */

const actorNameOf = (user: UserProfile | null | undefined): string =>
    user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Buchhaltung' : 'Buchhaltung';

/** Referenz der Buchungszeile. Der Zufallsteil trennt zwei Vorgaenge am selben Tag. */
const newReference = (): string =>
    `${MANUAL_BILLING_REFERENCE_PREFIX}${new Date().getFullYear()}-${Math.floor(
        Math.random() * 100000,
    )
        .toString()
        .padStart(5, '0')}`;

/**
 * Nach dem Schliessen der letzten offenen Menge das Projekt selbst ins Archiv
 * setzen — die Regeln dafuer stehen in projectAutoArchive und sind streng
 * (siehe dort: lieber gar nicht als zu frueh).
 */
const withAutoArchive = async (
    projectId: string,
    globalData: any,
    services: ServiceItem[],
    serviceLog: ServiceTransaction[],
    now: string,
    actor: string,
): Promise<{ globalData: any; notes: any[]; hinweis: string }> => {
    // Uebungsprojekt (Lernzentrum 8.2): kein Auto-Archiv. Das Kennzeichen steht
    // im Blob, weil dieser Pfad die Projektzeile nicht liest.
    const verdict = autoArchiveVerdict(services, globalData?.status, istUebungsprojekt({ istUebung: globalData?.istUebung }));
    if (!verdict.archive) {
        return { globalData, notes: [], hinweis: verdict.reason ? ` Nicht archiviert: ${verdict.reason}` : '' };
    }
    // ARCHIV-RIEGEL: der dritte Weg ins Archiv — und der einzige ohne
    // Anwenderklick. Ein Projekt mit eigenem offenem Posten bleibt auch hier
    // offen, und eine Mutter mit offenen Toechtern ebenfalls.
    // Gerade hier ist der Riegel noetig: "am LV der Mutter ist nichts mehr
    // offen" ist bei einer laufenden Tochter der NORMALZUSTAND zwischen zwei
    // Rechnungen. Ohne ihn setzte sich die Mutter genau in dem Moment selbst
    // ins Archiv, in dem die Buchhaltung die letzte offene Menge schliesst.
    //
    // Geprueft wird der STAND, DER GLEICH GESPEICHERT WIRD, nicht die
    // Datenbank: die traegt hier noch den Zustand von VOR dem Vorgang, und der
    // Riegel hielte dem Anwender die soeben geschlossene Menge als offen vor —
    // das Auto-Archiv griffe nie (Begruendung an
    // pruefeArchivierbarkeitFuerStand).
    //
    // Der Grund wird durchgereicht statt verschwiegen: "nichts passiert" ist
    // bei einem Automatismus die schlechteste Auskunft (siehe
    // AutoArchiveVerdict.reason). Die Regel steht in archiveGuardLogic.ts.
    const urteil = await pruefeArchivierbarkeitFuerStand(projectId, services, serviceLog);
    if (!urteil.erlaubt) {
        return { globalData, notes: [], hinweis: `\n\n${urteil.meldung}` };
    }
    return {
        globalData: { ...globalData, status: 'completed' },
        notes: [
            {
                id: crypto.randomUUID(),
                text: 'Projekt automatisch auf „Beendet" gesetzt: es ist nichts mehr abrechenbar und keine beauftragte Leistung mehr offen. Ins Archiv geht es, sobald die Schlussrechnung gestellt und bezahlt ist.',
                date: now,
                author: actor,
            },
        ],
        hinweis: ' Das Projekt wurde automatisch auf „Beendet" gesetzt.',
    };
};

export interface ManualBillingCommand {
    projectId: string;
    serviceId: string;
    /** Menge, die zusaetzlich als abgerechnet gilt. Bei der Ruecknahme ohne Bedeutung. */
    quantity?: number;
    /** Tag der Abrechnung (JJJJ-MM-TT). Bei der Ruecknahme ohne Bedeutung. */
    billedOn?: string;
    invoiceNumber?: string;
    reason?: string;
    customerId?: string;
    /** Der Teilnehmer der Aufteilung (project_splits.id). */
    splitPartyId?: string;
    splitProject?: boolean;
    /** Nur bei der Ruecknahme: welche Buchungszeile zurueckgenommen wird. */
    targetEntryId?: string;
    user: UserProfile | null;
}

export interface ManualBillingOutcome {
    ok: boolean;
    /** Fuer die Oberflaeche: was schiefging bzw. was geschah. */
    message: string;
}

/**
 * Gemeinsamer Rumpf von Buchen und Zuruecknehmen.
 *
 * Frisch geladen wird IMMER: die Liste in der Buchhaltungsansicht ist ein Abzug
 * vom letzten Refresh, und der Vorgang rechnet gegen `quantityUsed` und
 * `quantityBilled`. Auf einem veralteten Stand koennte er eine Menge buchen,
 * die inzwischen ueber eine echte Rechnung gelaufen ist — eine Doppelforderung
 * an den Kunden.
 */
const runManualBillingCommand = async (
    command: ManualBillingCommand,
    build: (
        service: ServiceItem,
        log: any[],
        ctx: { now: string; entryId: string; reference: string },
    ) => ReturnType<typeof planManualBilling>,
): Promise<ManualBillingOutcome> => {
    let fullProject: any;
    try {
        fullProject = await loadProjectById(command.projectId);
    } catch (e: any) {
        return { ok: false, message: `Projekt konnte nicht geladen werden: ${e?.message || e}` };
    }
    if (!fullProject) return { ok: false, message: 'Projekt nicht gefunden.' };

    const meta = fullProject.data?.metadata as ReportMetadata | undefined;
    const services: ServiceItem[] = meta?.globalData?.services || [];
    const target = services.find((s) => s.id === command.serviceId);
    if (!meta || !target) {
        return { ok: false, message: 'Die Position wurde im Projekt nicht gefunden.' };
    }

    const now = new Date().toISOString();
    // PAKETPOSITION: das Gate (Bestandteile offen/unbekannt) und der Deckel
    // prueft planManualBilling gegen GENAU diesen frischen Stand —
    // loadProjectById liest `project_services` mit `select *`, die Position
    // traegt also `bestandteile` und das von der Datenbank gepflegte
    // `bestandteileOffen` (projectLv.bestandteilFelderAusZeile).
    const result = build(target, getServiceLog({ metadata: meta }), {
        now,
        entryId: crypto.randomUUID(),
        reference: newReference(),
    });
    // Laut und ohne Schreibvorgang scheitern: still zu scheitern ist hier
    // besonders teuer, weil der Anwender sonst glaubt, den Fall erledigt zu
    // haben, und die Position beim naechsten Rechnungslauf wieder auftaucht.
    if (!result.ok || !result.plan) {
        return { ok: false, message: result.error || 'Die Buchung konnte nicht gespeichert werden.' };
    }

    const plan: ManualBillingPlan = result.plan;
    const updatedServices = services.map((s) => (s.id === target.id ? plan.service : s));
    const note = {
        id: crypto.randomUUID(),
        text: plan.noteText,
        date: now,
        author: actorNameOf(command.user),
    };

    // EINMAL gebaut und fuer Riegel UND Speichern benutzt: der Archiv-Riegel
    // muss exakt das Protokoll sehen, das gleich in der Datenbank steht.
    const neuesLog: ServiceTransaction[] = [plan.entry, ...(meta.globalData?.serviceLog || [])];
    const archiv = await withAutoArchive(fullProject.id, meta.globalData, updatedServices, neuesLog, now, actorNameOf(command.user));

    try {
        await saveProjectToCloud(
            {
                ...fullProject.data,
                metadata: {
                    ...meta,
                    globalData: {
                        ...archiv.globalData,
                        services: updatedServices,
                        serviceLog: neuesLog,
                        notes: [...archiv.notes, note, ...(meta.globalData?.notes || [])],
                    },
                },
            },
            fullProject.id,
        );
    } catch (e: any) {
        return { ok: false, message: `Speichern fehlgeschlagen: ${e?.message || e}` };
    }

    // Zweitschrift ins Journal — erst nach dem BESTAETIGTEN Speichern und
    // ausserhalb des try darueber: das Anhaengen darf den Ausgang dieses
    // Vorgangs nicht mehr aendern. Die Ruecknahme laeuft durch denselben
    // Rumpf und ist ebenfalls ein ANHANG (Gegenbuchung mit negativer Menge),
    // kein Loeschen — deshalb dieselbe Quelle.
    await mirrorEntriesToJournal({
        projectId: fullProject.id,
        entries: [plan.entry],
        previousLog: getServiceLog({ metadata: meta }),
        source: 'manual_billing',
        linkedProjectConfig: meta.globalData?.linkedProjectConfig ?? null,
    });

    return { ok: true, message: plan.noteText + archiv.hinweis };
};

/** Eine in der Vergangenheit abgerechnete Menge von Hand als abgerechnet buchen. */
export const bookManualBilling = (command: ManualBillingCommand): Promise<ManualBillingOutcome> =>
    runManualBillingCommand(command, (service, _log, ctx) =>
        planManualBilling(service, {
            quantity: command.quantity ?? 0,
            billedOn: command.billedOn ?? '',
            invoiceNumber: command.invoiceNumber,
            reason: command.reason,
            customerId: command.customerId,
            splitPartyId: command.splitPartyId,
            splitProject: command.splitProject,
            actorName: actorNameOf(command.user),
            actorId: command.user?.id,
            ...ctx,
        }),
    );

/** Eine manuelle Verbuchung zuruecknehmen — die Menge steht danach wieder zur Abrechnung. */
export const undoManualBilling = (command: ManualBillingCommand): Promise<ManualBillingOutcome> =>
    runManualBillingCommand(command, (service, log, ctx) =>
        planManualBillingUndo(service, log, {
            targetEntryId: command.targetEntryId || '',
            reason: command.reason,
            actorName: actorNameOf(command.user),
            actorId: command.user?.id,
            ...ctx,
        }),
    );
